'use client';

import { useEffect, useRef, useState } from 'react';
import { STATE_TRANSITION_DELAY_MS } from '../../constants/interaction/constants';

interface UseCountdownDelayOptions {
  remainingSeconds: number;
  isRunning: boolean;
  delayMs?: number;
}

interface UseCountdownDelayReturn {
  displaySeconds: number;
}

/**
 * Freezes the displayed countdown value for a short delay whenever the
 * running state *transitions*, then resumes showing the live value.
 * Does NOT freeze on the initial render — only on actual isRunning changes.
 * Pure React (no DOM) — safe for both web and mobile.
 */
export function useCountdownDelay({
  remainingSeconds,
  isRunning,
  delayMs = STATE_TRANSITION_DELAY_MS,
}: UseCountdownDelayOptions): UseCountdownDelayReturn {
  const [isPaused, setIsPaused] = useState(false);
  const frozenSecondsRef = useRef(remainingSeconds);
  const latestSecondsRef = useRef(remainingSeconds);
  latestSecondsRef.current = remainingSeconds;

  const prevIsRunningRef = useRef(isRunning);

  useEffect(() => {
    if (prevIsRunningRef.current === isRunning) {
      return;
    }
    prevIsRunningRef.current = isRunning;

    frozenSecondsRef.current = latestSecondsRef.current;
    setIsPaused(true);
    const timer = setTimeout(() => {
      setIsPaused(false);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [isRunning, delayMs]);

  const displaySeconds = isPaused ? frozenSecondsRef.current : remainingSeconds;

  return { displaySeconds };
}
